import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  CardHeader,
  DataTable,
  Disclosure,
  Drawer,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SectionHeader,
  SegmentedControl,
  SkeletonTable,
  StatTile,
  TrendChart,
  cn,
  type PillTone,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  BALANCE_SOURCE,
  CASH_SCENARIO,
  GRAVY_MODE,
  PARAMETER_SOURCE,
  WEEK_ROW_KIND,
  buildChartData,
  buildWeekRows,
  calibratedNumber,
  describeIncludedLines,
  describeParameters,
  formatMoney,
  formatMoneyCompact,
  formatRate,
  humanize,
  lineRates,
  longDate,
  shortDate,
  staleAccounts,
  STALE_BALANCE_DAYS,
  useCashFlow,
  periodLabel,
  weekLabel,
  type BalanceSource,
  type CashFlowReport,
  type CashScenario,
  type ForecastItem,
  type ParameterSource,
  type PaydayRow,
  type WeekCell,
  type WeekRow,
} from '@/lib/cash-flow';
import { filterRows, sortRows } from '@/lib/facet-table';
import { ACCOUNTING_PATH } from '@/lib/finance';
import { salesforceOpportunityUrl } from '@/lib/sales';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlSearch } from '@/lib/use-list-query-state';

/** URL keys for the page's controls, so a view can be sent to a colleague. */
const CASH_FLOW_PARAM = {
  scenario: 'scenario',
  gravy: 'gravy',
} as const;
/**
 * Links saved before the gravy band: `pipeline=1` (the pipeline band) and
 * `bookings=1` (new bookings) both meant "show what is not signed yet", which
 * is the gravy band now. `bookings=0` is ignored.
 */
const LEGACY_GRAVY_PARAMS = ['pipeline', 'bookings'] as const;
const FLAG_OFF = '0';

const GRAVY_VIEW = {
  hidden: 'hidden',
  shown: 'shown',
} as const;
type GravyView = (typeof GRAVY_VIEW)[keyof typeof GRAVY_VIEW];

const GRAVY_OPTIONS = [
  { key: GRAVY_VIEW.shown, label: 'With expected bookings', shortLabel: 'Expected' },
  { key: GRAVY_VIEW.hidden, label: 'Locked in only' },
] as const;

const SCENARIO_OPTIONS = [
  { key: CASH_SCENARIO.base, label: 'Base' },
  { key: CASH_SCENARIO.downside, label: 'Downside' },
] as const;

const SCENARIO_SUBTITLE: Readonly<Record<CashScenario, string>> = {
  [CASH_SCENARIO.base]:
    'The next 13 weeks of cash from what is locked in: signed events, deals with a contract out, payroll, bills and regular spend. Click any number to see what is behind it.',
  [CASH_SCENARIO.downside]:
    'The downside case: collections cut by the haircut and paid later, with no expected bookings. Click any number to see what is behind it.',
};

const SOURCE_LABEL: Readonly<Record<BalanceSource, string>> = {
  [BALANCE_SOURCE.manual]: 'Entered by hand',
  [BALANCE_SOURCE.quickbooks]: 'QuickBooks',
};

const PARAMETER_TONE: Readonly<Record<ParameterSource, { readonly label: string; readonly tone: PillTone }>> = {
  [PARAMETER_SOURCE.history]: { label: 'Measured', tone: 'success' },
  [PARAMETER_SOURCE.salesforce]: { label: 'Salesforce', tone: 'brand' },
  [PARAMETER_SOURCE.override]: { label: 'Override', tone: 'info' },
  [PARAMETER_SOURCE.default]: { label: 'Default', tone: 'warning' },
};

/** Events and open deals open in Salesforce; bills, runs and manual entries have no page to open. */
const LINKED_ITEM_KINDS: ReadonlySet<string> = new Set(['event', 'opportunity']);

const BELOW_MINIMUM_COLOR = '#be123c';
/** The gravy band's running total row (buildWeekRows' key). */
const GRAVY_CLOSING_KEY = 'gravy:closing';

interface DrillDown {
  readonly title: string;
  readonly weekStart: string;
  readonly cell: WeekCell;
}

/** The item list behind one week-table cell. */
function DrillDownList({ items }: { readonly items: readonly ForecastItem[] }): React.ReactElement {
  const sorted = [...items].sort((a, b) => b.amount - a.amount);
  return (
    <ul className="divide-y divide-portal-line rounded-portal-card border border-portal-line">
      {sorted.map((item) => (
        <li key={`${item.kind}:${item.refId}:${item.date}:${item.label}`} className="flex items-start justify-between gap-4 p-3">
          <div className="min-w-0">
            {LINKED_ITEM_KINDS.has(item.kind) ? (
              <a
                href={salesforceOpportunityUrl(item.refId)}
                target="_blank"
                rel="noreferrer"
                className="block text-portal-small font-bold text-brand hover:underline"
              >
                {item.label}
              </a>
            ) : (
              <span className="block text-portal-small font-bold text-portal-ink">{item.label}</span>
            )}
            <span className="block text-portal-micro text-portal-muted">
              {humanize(item.kind)} · {shortDate(item.date)}
              {item.probability !== undefined && item.fullAmount !== undefined
                ? ` · ${Math.round(item.probability * 100)}% of ${formatMoney(item.fullAmount)}`
                : ''}
            </span>
          </div>
          <span className="whitespace-nowrap text-portal-small font-bold text-portal-ink tabular-nums">
            {formatMoney(item.amount)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** One money cell of the week table; a button when there are items behind it. */
function WeekValue({
  row,
  cell,
  weekStart,
  belowMinimum,
  onOpen,
}: {
  readonly row: WeekRow;
  readonly cell: WeekCell;
  readonly weekStart: string;
  readonly belowMinimum: boolean;
  readonly onOpen: (drill: DrillDown) => void;
}): React.ReactElement {
  const isClosing = row.key === 'closing';
  const flagged = isClosing && belowMinimum;
  const emphasis = row.kind === WEEK_ROW_KIND.total || row.kind === WEEK_ROW_KIND.balance || row.kind === WEEK_ROW_KIND.net;
  const tone =
    row.kind === WEEK_ROW_KIND.net
      ? cell.amount < 0
        ? 'text-rose-700'
        : cell.amount > 0
          ? 'text-portal-success'
          : 'text-portal-muted'
      : row.kind === WEEK_ROW_KIND.gravy
        ? 'text-portal-muted'
        : cell.amount === 0
          ? 'text-portal-muted'
          : 'text-portal-ink';
  const isLineRow = row.kind === WEEK_ROW_KIND.line || (row.kind === WEEK_ROW_KIND.gravy && row.key !== GRAVY_CLOSING_KEY);
  const text = cell.amount === 0 && isLineRow ? '—' : formatMoneyCompact(cell.amount);
  return (
    <td
      className={cn(
        'whitespace-nowrap p-3 text-right text-portal-small tabular-nums',
        emphasis && 'font-bold',
        flagged ? 'bg-rose-50 text-rose-700' : tone,
      )}
      title={formatMoney(cell.amount)}
    >
      {cell.items.length > 0 ? (
        <button
          type="button"
          onClick={() => onOpen({ title: row.label, weekStart, cell })}
          className="rounded-sm text-brand underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-brand"
          aria-label={`${row.label}, ${weekLabel(weekStart)}: ${formatMoney(cell.amount)}. Show what is behind it`}
        >
          {text}
        </button>
      ) : (
        text
      )}
      {flagged ? <span className="block text-portal-micro font-bold">Below min.</span> : null}
    </td>
  );
}

function WeekTable({
  report,
  showGravy,
  onOpen,
}: {
  readonly report: CashFlowReport;
  readonly showGravy: boolean;
  readonly onOpen: (drill: DrillDown) => void;
}): React.ReactElement {
  const rows = useMemo(
    () => buildWeekRows(report.weeks, { showGravy, gravyMode: report.gravyMode, rates: lineRates(report.parameters) }),
    [report.weeks, report.gravyMode, report.parameters, showGravy],
  );
  const headers = ['Cash line', ...report.weeks.map((week) => periodLabel(week))];
  const rightAligned = headers.map((_, index) => index).filter((index) => index > 0);
  return (
    <DataTable headers={headers} rightAlignedHeaders={rightAligned} minWidth={220 + report.weeks.length * 96} stickyFirstColumn>
      {rows.map((row) => {
        if (row.kind === WEEK_ROW_KIND.section) {
          return (
            <tr key={row.key} className="border-t border-portal-line bg-portal-bg align-middle">
              <th
                scope="rowgroup"
                colSpan={1}
                className="sticky left-0 bg-portal-bg p-3 text-left text-portal-micro font-extrabold uppercase text-portal-muted"
              >
                {row.label}
              </th>
              <td colSpan={report.weeks.length} className="bg-portal-bg" />
            </tr>
          );
        }
        const strong = row.kind !== WEEK_ROW_KIND.line && row.kind !== WEEK_ROW_KIND.gravy;
        const rowBg = row.key === 'closing' ? 'bg-portal-sky' : 'bg-white';
        return (
          <tr
            key={row.key}
            className={cn(
              'border-t border-portal-line align-middle',
              row.kind === WEEK_ROW_KIND.total && 'border-t-2',
              rowBg,
            )}
          >
            <th
              scope="row"
              className={cn(
                // On a phone the pinned label wraps inside 40% of the screen, so the
                // week figures beside it stay visible instead of the label filling the card.
                'sticky left-0 z-[1] p-3 text-left text-portal-small max-md:w-[40vw] max-md:min-w-[8.5rem] md:whitespace-nowrap',
                rowBg,
                strong ? 'font-bold text-portal-ink' : 'font-normal text-portal-ink',
                row.kind === WEEK_ROW_KIND.line && 'pl-6',
                row.kind === WEEK_ROW_KIND.gravy && 'italic text-portal-muted',
              )}
            >
              {row.label}
            </th>
            {row.cells.map((cell, index) => {
              const week = report.weeks[index];
              if (!week) return null;
              return (
                <WeekValue
                  key={week.weekStart}
                  row={row}
                  cell={cell}
                  weekStart={week.weekStart}
                  belowMinimum={week.belowMinimum}
                  onOpen={onOpen}
                />
              );
            })}
          </tr>
        );
      })}
    </DataTable>
  );
}

function CashTodayPanel({ report }: { readonly report: CashFlowReport }): React.ReactElement {
  const accounts = report.opening.accounts;
  return (
    <Panel className="p-5">
      <CardHeader
        title="Cash today"
        description="Where the opening balance comes from, account by account."
        aside={
          <Button variant="secondary" size="compact" to={ACCOUNTING_PATH.assumptions}>
            Enter balances
          </Button>
        }
      />
      {accounts.length === 0 ? (
        <EmptyState
          title="No accounts yet"
          message="Enter this week's bank balances on the Assumptions page to start the forecast from real cash."
          action={
            <Button size="compact" to={ACCOUNTING_PATH.assumptions}>
              Enter balances
            </Button>
          }
        />
      ) : (
        <ul className="divide-y divide-portal-line">
          {accounts.map((account) => (
            <li key={account.accountId} className="flex items-start justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <span className="block text-portal-small font-bold text-portal-ink">{account.name}</span>
                <span className="block text-portal-micro text-portal-muted">
                  {SOURCE_LABEL[account.source]}
                  {account.asOf ? ` · as of ${shortDate(account.asOf)}` : ''}
                  {account.kind === 'credit_card' ? ' · card balance owed' : ''}
                </span>
                {account.missing ? (
                  <Pill tone="warning" className="mt-1">
                    No balance yet
                  </Pill>
                ) : null}
              </div>
              <span
                className={cn(
                  'whitespace-nowrap text-portal-small font-bold tabular-nums',
                  account.contribution < 0 ? 'text-rose-700' : 'text-portal-ink',
                )}
              >
                {formatMoney(account.contribution)}
              </span>
            </li>
          ))}
          <li className="flex items-center justify-between gap-3 pt-2.5">
            <span className="text-portal-small font-bold text-portal-ink">Total</span>
            <span className="text-portal-small font-bold text-portal-ink tabular-nums">{formatMoney(report.opening.total)}</span>
          </li>
        </ul>
      )}
    </Panel>
  );
}

/** The lines the payday table explains, listed above it. */
const PAYROLL_LINE_KEYS: ReadonlySet<string> = new Set(['payroll', 'commissions', 'benefits']);

/** The payday table's sort keys (fields of PaydayTableRow). */
const PAYDAY_SORT_FIELDS = {
  date: 'date',
  transfer: 'transfer',
  commissions: 'commissions',
  excludingCommissions: 'excludingCommissions',
} as const;
const PAYDAY_SORT_KEYS = Object.values(PAYDAY_SORT_FIELDS);
const PAYDAY_DEFAULT_SORT = { key: PAYDAY_SORT_FIELDS.date, direction: 'desc' } as const;

interface PaydayTableRow extends PaydayRow {
  readonly dateText: string;
}
const PAYDAY_SEARCH_FIELDS = ['dateText'] as const satisfies ReadonlyArray<keyof PaydayTableRow>;

/** Recent paydays: what BCE sent Team Building Alliance, the commissions paid with it, and the rest. Totals only. */
function PaydayTable({ paydays }: { readonly paydays: readonly PaydayRow[] }): React.ReactElement {
  const { sort, onSort } = useTableSort(PAYDAY_SORT_KEYS, PAYDAY_DEFAULT_SORT);
  const { search, setSearch } = useUrlSearch();
  const all = useMemo<PaydayTableRow[]>(() => paydays.map((payday) => ({ ...payday, dateText: longDate(payday.date) })), [paydays]);
  const rows = useMemo(() => sortRows(filterRows(all, search, PAYDAY_SEARCH_FIELDS), sort), [all, search, sort]);
  if (paydays.length === 0) {
    return (
      <Panel>
        <EmptyState
          title="No paydays found"
          message="Payroll runs appear here once the Team Building Alliance fundings are in QuickBooks."
        />
      </Panel>
    );
  }
  return (
    <>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search paydays"
          placeholder="Date"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>
      {rows.length === 0 ? (
        <Panel>
          <EmptyState title="No paydays match" message="Try a different search." />
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Payday', sortKey: PAYDAY_SORT_FIELDS.date },
            { label: 'BCE transfer', sortKey: PAYDAY_SORT_FIELDS.transfer },
            { label: 'Commissions paid (Salesforce)', sortKey: PAYDAY_SORT_FIELDS.commissions },
            { label: 'Transfer less commissions', sortKey: PAYDAY_SORT_FIELDS.excludingCommissions },
            'Payroll rate',
          ]}
          rightAlignedHeaders={[1, 2, 3]}
          sort={sort}
          onSort={onSort}
          minWidth={720}
          cards={rows.map((row) => ({
            key: row.date,
            title: row.dateText,
            subtitle: row.setsRate ? 'Sets the payroll rate' : '',
            fields: [
              { label: 'BCE transfer', value: formatMoney(row.transfer) },
              { label: 'Commissions', value: formatMoney(row.commissions) },
              { label: 'Less commissions', value: formatMoney(row.excludingCommissions) },
            ],
          }))}
        >
          {rows.map((row) => (
            <tr key={row.date} className="border-t border-portal-line align-middle">
              <td className="whitespace-nowrap p-3 text-portal-small font-bold text-portal-ink">{row.dateText}</td>
              <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">{formatMoney(row.transfer)}</td>
              <td className="whitespace-nowrap p-3 text-right text-portal-small tabular-nums">
                {formatMoney(row.commissions)}
                <span className="block text-portal-micro text-portal-muted">
                  {row.commissionCount} {row.commissionCount === 1 ? 'payment' : 'payments'}
                </span>
              </td>
              <td className="whitespace-nowrap p-3 text-right text-portal-small font-bold tabular-nums">
                {formatMoney(row.excludingCommissions)}
              </td>
              <td className="p-3 text-portal-small">
                {row.setsRate ? <Pill tone="info">Sets the rate</Pill> : <span className="text-portal-muted">—</span>}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
    </>
  );
}

/**
 * What each cash line holds and where its numbers come from, with the
 * paydays behind the payroll and commissions lines. Collapsed by default.
 */
function WhatsIncluded({ report }: { readonly report: CashFlowReport }): React.ReactElement {
  const lines = describeIncludedLines(report);
  const payroll = report.payroll;
  return (
    <>
      <SectionHeader
        title="What's included"
        description="What each line holds and where its numbers come from, starting with payroll."
      />
      <Disclosure
        summary={
          <span className="text-portal-small text-portal-ink">
            {payroll
              ? `Payroll ${formatMoney(payroll.rate.value)} a run plus ${formatMoney(payroll.commissions.value)} commissions; benefits ${formatMoney(payroll.benefits.value)} a month`
              : `${lines.length} cash lines`}
          </span>
        }
      >
        <div className="space-y-6">
          {payroll ? (
            <div className="space-y-3">
              <ul className="divide-y divide-portal-line">
                {lines
                  .filter((line) => PAYROLL_LINE_KEYS.has(line.key))
                  .map((line) => (
                    <IncludedRow key={line.key} label={line.label} includes={line.includes} source={line.source} />
                  ))}
              </ul>
              <PaydayTable paydays={payroll.paydays} />
            </div>
          ) : null}
          <ul className="divide-y divide-portal-line">
            {lines
              .filter((line) => !payroll || !PAYROLL_LINE_KEYS.has(line.key))
              .map((line) => (
                <IncludedRow key={line.key} label={line.label} includes={line.includes} source={line.source} />
              ))}
          </ul>
        </div>
      </Disclosure>
    </>
  );
}

function IncludedRow({
  label,
  includes,
  source,
}: {
  readonly label: string;
  readonly includes: string;
  readonly source: string;
}): React.ReactElement {
  return (
    <li className="grid gap-1 py-2.5 sm:grid-cols-[240px_1fr] sm:gap-4">
      <span className="text-portal-small font-bold text-portal-ink">{label}</span>
      <span className="text-portal-small text-portal-ink">
        {includes}
        <span className="block text-portal-micro text-portal-muted">Source: {source}</span>
      </span>
    </li>
  );
}

function HowCalculated({ report }: { readonly report: CashFlowReport }): React.ReactElement {
  const rows = describeParameters(report.parameters);
  const excluded = Object.values(report.overheadDecomposition.excluded).reduce((sum, value) => sum + value, 0);
  const measured = rows.filter((row) => row.source === PARAMETER_SOURCE.history).length;
  return (
    <>
      <SectionHeader
        title="How this is calculated"
        description="Each assumption, its value, and whether it was measured from BCE's history or is a default."
      />
      <Disclosure
        summary={
          <span className="text-portal-small text-portal-ink">
            {rows.length} assumptions: {measured} measured, {rows.length - measured} set or default
          </span>
        }
      >
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <ul className="divide-y divide-portal-line">
          {rows.map((row) => (
            <li key={row.key} className="grid gap-1 py-2.5 sm:grid-cols-[200px_1fr_auto] sm:items-center sm:gap-4">
              <span className="text-portal-small font-bold text-portal-ink">{row.label}</span>
              <span className="text-portal-small text-portal-ink">
                {row.value}
                <span className="block text-portal-micro text-portal-muted">{row.basis}</span>
              </span>
              <Pill tone={PARAMETER_TONE[row.source].tone}>{PARAMETER_TONE[row.source].label}</Pill>
            </li>
          ))}
        </ul>
        <div className="space-y-3 text-portal-small text-portal-ink">
          <p>
            <span className="font-bold">Minimum cash:</span> {formatMoney(report.minimumCash)}. Weeks that close below it are
            flagged in the chart and the table.
          </p>
          <p>
            <span className="font-bold">Unlikely to be collected:</span> {formatMoney(report.atRisk.amount)} of open balances
            the collection curve and the cancellation rate expect never to arrive. It is left out of cash in.
          </p>
          <p>
            <span className="font-bold">After the 13 weeks:</span> {formatMoney(report.beyondHorizon)} of expected collections
            land later than the horizon.
          </p>
          <p>
            <span className="font-bold">Overhead run rate:</span> built from {formatMoney(report.overheadDecomposition.included)}{' '}
            of recent card and bank spend; {formatMoney(excluded)} was left out because payroll, bills, event costs and ads are
            forecast on their own lines.
          </p>
          <RateNote report={report} />
          <p>
            <span className="font-bold">Expected bookings:</span> cash that is not locked in yet, shown on top of the locked-in
            closing cash and never in the low-cash emails. It holds deals with no contract out yet, at each stage's odds of
            signing
            {report.gravyIncludesRunRate
              ? ', and new bookings at the recent weekly signing rate, less the deals already counted so nothing is counted twice'
              : ''}
            .
          </p>
        </div>
      </div>
      </Disclosure>
    </>
  );
}

/** The two measured rates behind the base case, each with what it is based on. */
function RateNote({ report }: { readonly report: CashFlowReport }): React.ReactElement {
  const contract = calibratedNumber(report.parameters, 'contractToSigned');
  const cancellation = calibratedNumber(report.parameters, 'cancellationRate');
  const basis = (rate: typeof contract, unit: string): string => {
    if (!rate) return '';
    if (rate.source === PARAMETER_SOURCE.override) return ' (set on the Assumptions page)';
    if (rate.source === PARAMETER_SOURCE.default) return ' (not enough history yet, so the last measured value)';
    return `, based on ${rate.sampleSize.toLocaleString('en-US')} ${unit} over 2 years`;
  };
  return (
    <p>
      <span className="font-bold">Base case:</span> signed events, plus deals with a contract out but not signed yet.
      {contract ? ` ${formatRate(contract.value)} of contracts sign${basis(contract, 'deals')}.` : ''}
      {cancellation
        ? ` ${formatRate(cancellation.value)} of signed events cancel${basis(cancellation, 'signed deals')}, so events not held yet count at ${formatRate(1 - cancellation.value)}.`
        : ''}
    </p>
  );
}

/**
 * The nudge when the forecast starts from an old number: any account whose
 * balance is more than a week old (or missing), with the way to fix it.
 */
function StaleBalanceNotice({ report }: { readonly report: CashFlowReport }): React.ReactElement | null {
  const stale = staleAccounts(report.opening.accounts, report.asOf);
  if (stale.length === 0) return null;
  const described = stale
    .map((account) => (account.asOf ? `${account.name} (${longDate(account.asOf)})` : `${account.name} (no balance yet)`))
    .join(', ');
  const message =
    stale.length === 1
      ? `${described} is more than ${STALE_BALANCE_DAYS} days old, so cash today may be off.`
      : `${stale.length} balances are more than ${STALE_BALANCE_DAYS} days old, so cash today may be off: ${described}.`;
  return (
    <ErrorNotice
      inline
      tone="warning"
      title="Old balances."
      message={message}
      action={
        <Button size="compact" variant="secondary" to={ACCOUNTING_PATH.assumptions}>
          Enter this week's balances
        </Button>
      }
    />
  );
}

/**
 * Analytics → Accounting → Cash flow: the 13-week direct-method forecast.
 * Summary first (cash today, the low point, the first week below minimum,
 * the 13-week totals), then the chart, then the week-by-week table whose
 * every number drills down to the events, bills and runs behind it.
 * Behind view_accounting (route guard + API).
 */
export function CashFlowPage(): React.ReactElement {
  const [params, setParams] = useSearchParams();
  const scenario: CashScenario =
    params.get(CASH_FLOW_PARAM.scenario) === CASH_SCENARIO.downside ? CASH_SCENARIO.downside : CASH_SCENARIO.base;
  // Expected bookings show by default; only an explicit off hides them.
  const gravyView: GravyView =
    params.get(CASH_FLOW_PARAM.gravy) === FLAG_OFF ? GRAVY_VIEW.hidden : GRAVY_VIEW.shown;
  const [drill, setDrill] = useState<DrillDown | null>(null);

  const cashFlow = useCashFlow(scenario);
  const report = cashFlow.data;
  const gravyOff = report?.gravyMode === GRAVY_MODE.off;
  const showGravy = gravyView === GRAVY_VIEW.shown && !gravyOff;
  const chart = useMemo(() => (report ? buildChartData(report) : null), [report]);
  const firstBelow = report?.weeks.find((week) => week.weekStart === report.firstWeekBelowMinimum);

  function setParam(key: string, value: string | null): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value === null) next.delete(key);
        else next.set(key, value);
        // Rewrite an old link's flags into the one gravy flag.
        if (key === CASH_FLOW_PARAM.gravy) for (const legacy of LEGACY_GRAVY_PARAMS) next.delete(legacy);
        return next;
      },
      { replace: true },
    );
  }

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="ACCOUNTING"
        title="Cash flow"
        subtitle={`${SCENARIO_SUBTITLE[scenario]}${report ? ` As of ${longDate(report.asOf)}.` : ''}`}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <SegmentedControl<CashScenario>
          label="Scenario"
          value={scenario}
          onChange={(next) => setParam(CASH_FLOW_PARAM.scenario, next === CASH_SCENARIO.base ? null : next)}
          options={SCENARIO_OPTIONS}
        />
        <SegmentedControl<GravyView>
          label="Expected bookings"
          value={showGravy ? GRAVY_VIEW.shown : GRAVY_VIEW.hidden}
          onChange={(next) => setParam(CASH_FLOW_PARAM.gravy, next === GRAVY_VIEW.shown ? null : FLAG_OFF)}
          options={GRAVY_OPTIONS}
        />
      </div>
      <p className="-mt-2 mb-4 text-portal-micro text-portal-muted">
        {gravyOff
          ? 'The downside case leaves out expected bookings.'
          : `Expected bookings are ${report?.gravyIncludesRunRate === false ? '' : 'new bookings at the recent weekly rate plus '}deals with no contract out yet. They show as a separate line on top of the locked-in closing cash, which the low-cash emails use.`}
      </p>

      {cashFlow.isError ? (
        <ErrorNotice title="Cash flow unavailable" message={errorMessage(cashFlow.error)} />
      ) : cashFlow.isPending || !report || !chart ? (
        <SkeletonTable label="Building the forecast…" />
      ) : report.weeks.length === 0 ? (
        <Panel>
          <EmptyState
            title="No forecast yet"
            message="The forecast needs this week's bank balances to start from. Enter them on the Assumptions page."
            action={<Button to={ACCOUNTING_PATH.assumptions}>Enter balances</Button>}
          />
        </Panel>
      ) : (
        <>
          <StaleBalanceNotice report={report} />
          <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              label="Cash today"
              value={formatMoney(report.opening.total)}
              hint={`${report.opening.accounts.length} ${report.opening.accounts.length === 1 ? 'account' : 'accounts'}, banks less cards`}
            />
            <StatTile
              label="Low point"
              value={formatMoney(report.lowPoint.closing)}
              hint={weekLabel(report.lowPoint.weekStart).replace('Wk', 'Week')}
            />
            <StatTile
              label={`First week below ${formatMoneyCompact(report.minimumCash)}`}
              value={firstBelow ? shortDate(firstBelow.weekStart) : 'None'}
              hint={firstBelow ? `Closes at ${formatMoney(firstBelow.closing)}` : 'None in 13 weeks'}
            />
            <StatTile
              label="13-week net"
              value={formatMoney(report.totals.cashIn - report.totals.cashOut)}
              hint={`${formatMoneyCompact(report.totals.cashIn)} in, ${formatMoneyCompact(report.totals.cashOut)} out`}
            />
          </div>

          {report.firstWeekBelowMinimum ? (
            <div className="mb-4">
              <ErrorNotice
                inline
                tone="warning"
                title="Below minimum cash"
                message={`Cash is forecast to close below the ${formatMoney(report.minimumCash)} minimum in the week of ${longDate(report.firstWeekBelowMinimum)}.`}
              />
            </div>
          ) : null}

          <div className="mb-4 grid gap-4 xl:grid-cols-3">
            <Panel className="p-5 xl:col-span-2">
              <CardHeader
                title="Closing cash by week"
                description="Bars are each week's cash in (up) and cash out (down); the line is the cash left at the end of the week."
              />
              <TrendChart
                title="Closing cash and weekly cash flow"
                unit="Closing cash"
                points={chart.points}
                series={[
                  { key: 'in', label: 'Cash in', values: chart.cashIn },
                  { key: 'out', label: 'Cash out', values: chart.cashOut },
                ]}
                overlays={[
                  { key: 'closing', label: 'Closing cash', values: chart.closing, tone: 0 },
                  { key: 'minimum', label: `Minimum (${formatMoneyCompact(report.minimumCash)})`, values: chart.minimum, tone: 1 },
                  ...(showGravy
                    ? [{ key: 'gravy', label: 'Closing with expected bookings', values: chart.closingWithGravy, tone: 2 as const }]
                    : []),
                ]}
                markers={
                  report.firstWeekBelowMinimum
                    ? [{ x: report.firstWeekBelowMinimum.slice(0, 10), label: 'Below minimum', color: BELOW_MINIMUM_COLOR }]
                    : []
                }
                formatValue={formatMoneyCompact}
                height={320}
              />
            </Panel>
            <CashTodayPanel report={report} />
          </div>

          <WeekTable report={report} showGravy={showGravy} onOpen={setDrill} />

          <div className="mt-8">
            <WhatsIncluded report={report} />
          </div>
          <div className="mt-8">
            <HowCalculated report={report} />
          </div>
        </>
      )}

      <Drawer
        open={drill !== null}
        onClose={() => setDrill(null)}
        title={drill ? `${drill.title}, ${weekLabel(drill.weekStart).replace('Wk', 'week')}` : ''}
        {...(drill
          ? {
              subtitle: `${formatMoney(drill.cell.amount)} from ${drill.cell.items.length} ${drill.cell.items.length === 1 ? 'item' : 'items'}`,
            }
          : {})}
      >
        {drill ? <DrillDownList items={drill.cell.items} /> : null}
      </Drawer>
    </section>
  );
}
